import React, { useState } from "react";
import apiHelper from "../../../util/ApiHelper";
import { Link } from "react-router-dom";
import { useLoading } from "../../../util/LoadingProvider";
import { Box, Grid, Typography } from "@material-ui/core";
import { toast } from "react-toastify";
import {formatDatetime, modal} from "../../../util";
import FilmIconForm from "./filmIconForm";
import DragAndDropTable from "../../common/DragAndDropTable";

export default function FilmIconPage(props) {
  const { disabled, match } = props;
  const [globalFilter, setGlobalFilter] = useState("");
  const [isSearched, setIsSearched] = useState(false);
  const [orderingDisabled, setOrderingDisabled] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchString, setSearchString] = useState("");
  const { setLoading } = useLoading();
  const [pageSize, setPageSize] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [seq, setSeq] = useState(0);

  const getFilmIcons = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getFilmIcons({
        page: pageIndex,
        pageSize,
        offset: (pageIndex + 1) * pageSize,
        keyword: searchString
      });
      console.log(result);
      setRows(result.data.content.filter((x) => x));
      setTotalElements(result.data.totalElements);
      setTotalPages(result.data.totalPages);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, searchString, setLoading]);

  const getAllFilmIcons = async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getFilmIcons({
        pageSize: 99999999,
      });
      const maxSeq = result.data.content.reduce((x, y) => (
        Math.max(x.sequence ? x.sequence : 0, y.sequence ? y.sequence : 0)
      ), 0);
      setSeq(maxSeq + 1);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    getAllFilmIcons();
  }, [])

  React.useEffect(() => {
    if (pageSize) getFilmIcons();
  }, [getFilmIcons]);

  const handleSorting = (bool) => {
    if (!isSearched) {
      setOrderingDisabled(bool);
    }
  };

  const handleSearch = (searchString) => {
    setGlobalFilter(searchString);
    setIsSearched(true);
    setOrderingDisabled(true);
  };

  const getMaxSeqInRows = (rows) => {
    let seq = null;
    rows.forEach((e) => {
      if ((e.seq && seq === null) || (seq !== null && e.seq > seq)) {
        seq = e.seq
      }
    })
    return seq;
  }

  const saveOrdering = async () => {
    try {
      const maxSeq = getMaxSeqInRows(rows);
      const seqData = {};
      rows.forEach((e, i) => {
        seqData[e.id] = maxSeq - i;
      });
      await apiHelper.hkiff.orderFilmIcons(seqData);
      toast.dark("Your changes is saved.");
      await getFilmIcons();
    } catch (err) {
      console.log(err);
    }
  };

  const handleDelete = async (row) => {
    const decision = await modal.fire({
      title: "Delete File",
      text: "Are you sure to delete this icon?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const { id } = row;
        const result = await apiHelper.hkiff.deleteFilmIconById(id);
        if (result.status === 200) {
          toast.dark("The icon is deleted.");
          await getFilmIcons();
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Id",
        accessor: "id",
        Cell: ({row}) => {
            return (
                <Link
                    style={{
                        textDecoration: "none",
                        color: "#306F9D",
                    }}
                    to={`${match.url}/${row.original.id}`}
                >
                    {row.original.id}
                </Link>
            );
        },
    },
      {
        Header: "Title",
        accessor: "localizations.en.title",
        Cell: ({ row }) => {
          return (
            <Link
              style={{
                textDecoration: "none",
                color: "#306F9D",
              }}
              to={`${match.url}/${row.original.id}`}
            >
              {row.original.localizations.en.title}
            </Link>
          );
        },
      },
      {
        Header: "Icon",
        accessor: "icon",
        Cell: ({ row }) => {
          return (
              <img
                  src={row.original.icon}
                  alt=""
                  style={{ maxHeight: "30px", maxWidth: "30px" }}
              />
          );
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Background Color",
        accessor: "color",
        Cell: ({row}) => {
          return (
              row.original.color ?
                  <div style={{
                    width: "60px",
                    height: "30px",
                    backgroundColor: row.original.color
                  }}></div> :
                  <span>null</span>
          );
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Status",
        accessor: "status",
        disableGlobalFilter: true,
        Cell: ({row}) => {
          switch (row.original.status) {
            default:
            case 0:
              return <span
                  style={{
                    color: "#306F9D",
                  }}>Inactive</span>
            case 1:
              return <span
                  style={{
                    color: "#306F9D",
                  }}>Active</span>
          }
        },
      },
      {
        Header: "Last Update",
        accessor: "updateTime",
        disableGlobalFilter: true,
        Cell: ({row}) => {
          return formatDatetime(row.original.updateTime);
        },
      }
    ],
    []
  );

  return (
    <>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Film &gt; Film Icon
        </Typography>
      </Box>
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <FilmIconForm
            description="Add New Icon"
            submitBtnTxt="Add New Icon"
            gridSize={4}
            upperBtnGroupSize="75%"
            fetchData={getFilmIcons}
            disabled={disabled}
            seq={seq}
            setSeq={setSeq}
          />
        </Grid>
        <Grid item xs={8}>
          <DragAndDropTable
            globalFilter={globalFilter}
            orderingDisabled={orderingDisabled}
            handleSorting={handleSorting}
            handleSearch={handleSearch}
            searchString={searchString}
            setSearchString={setSearchString}
            fetchData={getFilmIcons}
            data={rows}
            setData={setRows}
            columns={columns}
            bulkDeleteApi={(ids) => Promise.all(ids.map((id) => apiHelper.hkiff.deleteFilmIconById(id)))}
            subject="icon"
            saveOrderingApi={saveOrdering}
            disabled={disabled}
            withPagination={true}
            editDeleteAction={true}
            handleDelete={handleDelete}
            deleteByRow={true}
            setParentPageSize={setPageSize}
            setParentPageIndex={setPageIndex}
            parentPageIndex={pageIndex}
            totalElements={totalElements}
            totalPages={totalPages}
          />
        </Grid>
      </Grid>
    </>
  );
}
